import { gql, useQuery } from '@apollo/client';
import {
  buildFriendlyConversationId,
  parseFriendlyConversationId,
} from '@provision/formatting-utils';
import { useMemo } from 'react';
import type { Conversation } from 'src/gql/graphql';
import type { Records } from 'src/graphql/types';

const GET_CONVERSATION_SUBJECTS_QUERY = gql`
  query GetConversationSubjects($params: QueryParamsWithSearch) {
    getConversation(queryParams: $params) {
      records {
        id
        conversationId
        partnerId
        workspaceConversationId
        subject
      }
    }
  }
`;

type ConversationSubjectRecord = Pick<
  Conversation,
  'conversationId' | 'partnerId' | 'workspaceConversationId' | 'subject'
>;

interface GetConversationSubjectsData {
  getConversation?: Records<ConversationSubjectRecord>;
}

interface ParsedCitation {
  partnerId: string;
  workspaceConversationId: string;
}

function parseCitations(friendlyIds: string[]): ParsedCitation[] {
  const seen = new Set<string>();
  const parsed: ParsedCitation[] = [];

  for (const friendlyId of friendlyIds) {
    if (seen.has(friendlyId)) {
      continue;
    }
    seen.add(friendlyId);

    const { partnerId, workspaceConversationId } = parseFriendlyConversationId(friendlyId);
    if (partnerId === null || workspaceConversationId === '') {
      continue;
    }
    parsed.push({ partnerId, workspaceConversationId });
  }

  return parsed;
}

export function useConversationSubjects(friendlyIds: string[]): {
  subjects: Map<string, string>;
  loading: boolean;
} {
  const citations = parseCitations(friendlyIds);
  const partnerIds = [...new Set(citations.map((citation) => citation.partnerId))];
  const workspaceIds = [
    ...new Set(citations.map((citation) => citation.workspaceConversationId)),
  ];

  const { data, loading } = useQuery<GetConversationSubjectsData>(
    GET_CONVERSATION_SUBJECTS_QUERY,
    {
      skip: citations.length === 0,
      variables: {
        params: {
          filters: [
            { attribute: 'partnerId', operator: 'in', value: partnerIds.join(',') },
            {
              attribute: 'workspaceConversationId',
              operator: 'in',
              value: workspaceIds.join(','),
            },
          ],
          limit: partnerIds.length * workspaceIds.length,
        },
      },
      onError: (queryError) => {
        console.error('[siteOverview] conversation subject lookup failed', queryError.message);
      },
    },
  );

  return useMemo(() => {
    const subjects = new Map<string, string>();
    for (const record of data?.getConversation?.records ?? []) {
      if (record?.subject) {
        subjects.set(
          buildFriendlyConversationId(record.partnerId, record.workspaceConversationId),
          record.subject,
        );
      }
    }
    return { subjects, loading };
  }, [data, loading]);
}
